"use client";

import React from "react";
import { ArrowRight, PhoneCall } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function ClosingCta() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLElement>({
    threshold: 0.15
  });
  return <section id="closingcta" data-nav="light" ref={ref} className="bg-background text-foreground py-20 lg:py-28 overflow-hidden transition-opacity duration-700" style={{
    opacity: isVisible ? 1 : 0
  }}>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <div className="lg:col-span-6 flex flex-col justify-center space-y-6">
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-foreground leading-tight">
              Ready to elevate your Brookhaven home?
            </h2>
            <p className="text-base sm:text-lg text-muted-foreground max-w-xl leading-relaxed">
              From tailored gourmet kitchens to full-scale interior transformations, we will follow up to discuss your project and bring craftsmanship, structure, and calm to every stage of your renovation.
            </p>
            <div className="pt-2 flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
              <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-md font-medium text-base h-12 px-7 rounded-md">
                <Link to="/ContactRequestAnEstimatePage">
                  Request an Estimate
                  <ArrowRight className="size-4 ml-2 inline-block" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="border-border hover:bg-muted text-foreground font-medium text-base h-12 px-6 rounded-md">
                <a href="tel:4045550184">
                  <PhoneCall className="size-4 mr-2 inline-block" />
                  (404) 555-0184
                </a>
              </Button>
            </div>
          </div>

          <div className="lg:col-span-6">
            <div className="relative grid grid-cols-12 gap-4">
              <div className="col-span-8 overflow-hidden rounded-xl shadow-lg border border-border">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_kitchen_renovation.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_kitchen_renovation.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_kitchen_renovation.png-wvc-sizes" alt="Custom renovated open-concept kitchen with quartz island and hardwood floors" loading="lazy" decoding="async" className="w-full h-72 sm:h-96 object-cover transition-transform duration-500 hover:scale-105" />
              </div>
              <div className="col-span-4 flex flex-col gap-4">
                <div className="overflow-hidden rounded-xl shadow-md border border-border h-full">
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/luxury_bathroom_suite.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/luxury_bathroom_suite.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/luxury_bathroom_suite.webp-wvc-sizes" alt="Master bathroom remodel featuring a freestanding bathtub and marble tiling" loading="lazy" decoding="async" className="w-full h-full min-h-[136px] object-cover transition-transform duration-500 hover:scale-105" />
                </div>
                <div className="overflow-hidden rounded-xl shadow-md border border-border h-full">
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/remodeled_living_space.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/remodeled_living_space.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/remodeled_living_space.webp-wvc-sizes" alt="Interior home renovation with built-in cabinetry and natural lighting" loading="lazy" decoding="async" className="w-full h-full min-h-[136px] object-cover transition-transform duration-500 hover:scale-105" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}